/**
 * pt-019 CV Banner Live Counter
 *
 * Animates a slot-machine-style digit counter inside the `.pt-019-cv-banner-live`
 * block. Numbers are sourced from `data-counter-entries`, a JSON object keyed by
 * hour (0–23), each containing an array of values to cycle through during that
 * hour. Digits are pre-rendered as static `<span>` strips in the DOM; only their
 * CSS `transform: translateY(...)` is updated — no HTML is ever rebuilt.
 */
(function () {
  // Time between each number step within the same hour bucket (ms).
  const STEP_DURATION = 2400;

  /**
   * Normalise a raw value to a 3-character zero-padded digit string.
   * Non-digit characters are stripped; only the last 3 digits are kept.
   * @param {*} value
   * @returns {string} e.g. "042", "511"
   */
  // [BOLD - AI - Code - Generated - 2026-08-13] digitCount param allows 2-block locales (e.g. pl_PL) to normalize correctly.
  function normalizeNumber(value, digitCount = 3) {
    const digitsOnly = String(value ?? "").replaceAll(/\D+/g, "");
    return digitsOnly.slice(-digitCount).padStart(digitCount, "0");
  }

  /**
   * Parse the `data-counter-entries` attribute from the counter element.
   * Expected shape: { "0": ["121","122",...], ..., "23": ["135","139",...] }
   * Falls back to an empty object on any parse failure.
   * @param {HTMLElement} display
   * @returns {Object}
   */
  function parseEntries(display) {
    try {
      const parsed = JSON.parse(display.dataset.counterEntries || "{}");
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      console.warn(
        "Invalid counter entries JSON for js-counter element.",
        error,
      );
      return {};
    }
  }

  /**
   * Return the normalised number sequence for a given hour.
   * @param {Object} entries  Parsed counter entries object.
   * @param {string|number} hour  Current hour (0–23).
   * @returns {string[]} Array of 3-digit strings, e.g. ["511","521","532"].
   */
  function getHourNumbers(entries, hour, digitCount = 3) {
    const hourEntries = entries[String(hour)];
    if (!Array.isArray(hourEntries) || !hourEntries.length) {
      return [];
    }

    return hourEntries.map((v) => normalizeNumber(v, digitCount)).filter(Boolean);
  }

  /**
   * Measure the rendered height of a single digit span inside a strip.
   * Falls back to 60px (the design-spec line-height) if the element is not
   * yet in the layout.
   * @param {HTMLElement} strip  A `.counter-digits` element.
   * @returns {number} Height in pixels.
   */
  function getDigitHeight(strip) {
    const sample = strip.querySelector("span");
    return sample ? sample.getBoundingClientRect().height : 60;
  }

  /**
   * Move each digit strip to display `nextNumber`.
   *
   * When `immediate` is true the change is instant (no animation) — used for
   * the initial render and when the hour bucket changes.
   * When `immediate` is false the CSS slot-machine transition plays — used for
   * regular in-bucket cycling.
   *
   * Inline `transition` is set explicitly before each update so the animated
   * path never accidentally relies on the CSS cascade being re-evaluated at
   * just the right moment (which can cause jumps under certain browser timing).
   *
   * @param {HTMLElement} display   The `.js-counter` root element.
   * @param {string|number} nextNumber  Target value to display.
   * @param {boolean} immediate  If true, snap without animation.
   */
  function setDisplay(display, nextNumber, immediate) {
    const digitCount = parseInt(display.dataset.counterDigits || "3", 10);
    const normalizedNext = normalizeNumber(nextNumber, digitCount);
    const strips = Array.from(display.querySelectorAll(".counter-digits"));

    // Guard: number of rendered strips must match digit count.
    if (strips.length !== normalizedNext.length) {
      return;
    }

    if (immediate) {
      // Disable transition, then force a reflow to commit it before transforms change.
      strips.forEach((strip) => {
        strip.style.transition = "none";
      });
      display.getBoundingClientRect();
    } else {
      // Set transition inline — do not rely on CSS class cascade persisting
      // after the previous immediate call cleared the inline style.
      strips.forEach((strip) => {
        strip.style.transition = "transform 1s ease-in-out";
      });
    }

    // Translate each strip so the target digit is visible in the viewport window.
    strips.forEach((strip, index) => {
      const digitHeight = getDigitHeight(strip);
      const targetDigit = Number.parseInt(normalizedNext[index], 10);
      strip.style.transform = `translateY(-${digitHeight * targetDigit + 60}px)`;
    });

    if (immediate) {
      // Second reflow commits the new transform position without animation,
      // then restore cascade control by clearing the inline override.
      display.getBoundingClientRect();
      strips.forEach((strip) => {
        strip.style.transition = "";
      });
    }
  }

  /**
   * Initialise one counter element: render the current value immediately,
   * then start the interval that cycles through that hour's number sequence.
   * Safe to call multiple times — a `data-counter-ready` flag prevents
   * duplicate intervals being attached.
   * @param {HTMLElement} display
   */
  function setupCounter(display) {
    if (display.dataset.counterReady === "true") {
      return;
    }

    const entries = parseEntries(display);
    const digitCount = parseInt(display.dataset.counterDigits || "3", 10);
    const initialHour = new Date().getHours();
    const initialNumbers = getHourNumbers(entries, initialHour, digitCount);
    // Start from the first value in the current hour's sequence.
    const initialNumber = normalizeNumber(initialNumbers[0] || "0".repeat(digitCount), digitCount);
    const state = {
      hour: String(initialHour),
      index: Math.max(initialNumbers.indexOf(initialNumber), 0),
    };

    // Snap to the starting number without animation.
    setDisplay(display, initialNumber, true);
    display.dataset.counterReady = "true";

    // `data-animate="false"` opts the element out of live cycling.
    if (display.dataset.animate === "false") {
      return;
    }

    globalThis.setInterval(() => {
      const activeHour = String(new Date().getHours());
      const numbers = getHourNumbers(entries, activeHour, digitCount);

      if (!numbers.length) {
        return;
      }

      // Hour has rolled over — jump (no animation) to the first value of the new bucket.
      if (state.hour !== activeHour) {
        state.hour = activeHour;
        state.index = 0;
        setDisplay(display, numbers[0], true);
        return;
      }

      // Advance to the next value in the current hour's sequence with animation.
      state.index = (state.index + 1) % numbers.length;
      setDisplay(display, numbers[state.index], false);
    }, STEP_DURATION);
  }

  /**
   * Find all counter elements inside the block and initialise each one.
   */
  function run() {
    // const counters = document.querySelectorAll(
    //   ".pt-019-cv-banner-live .js-counter",
    // );
    // counters.forEach(setupCounter);
    const counter = document.querySelector(
      ".cv-banner-live .js-counter",
    );
    if (counter) setupCounter(counter);
  }

  // Run after the DOM is ready so digit spans are measurable.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
})();
